// custom-pagination.component.ts
import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

export interface PaginationConfig {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  maxVisiblePages?: number;
  showFirstLast?: boolean;
  showPrevNext?: boolean;
  baseRoute?: string;
  queryParams?: { [key: string]: any };
  // Direction of the LAST page navigation. Set by the parent on every
  // page change so this component's window bias survives the destroy/
  // recreate cycle imposed by the parent's `*ngIf="!loadAPIcall …"`
  // wrapper. If absent, falls back to comparing the incoming currentPage
  // against the stored one — works while the component instance is
  // alive, but not across recreations.
  direction?: 'forward' | 'backward';
}

@Component({
  selector: 'app-custom-pagination',
  standalone: true,
  imports: [NgFor, NgIf, NgClass, NgTemplateOutlet, RouterModule],
  templateUrl: `./custom-pagination.component.html`,
  styleUrls: ['./custom-pagination.component.css'],
})
export class CustomPaginationComponent implements OnInit, OnChanges {
  @Input() config: PaginationConfig = {
    currentPage: 1,
    totalItems: 0,
    itemsPerPage: 10
  };
  
  @Input() showPageInfo: boolean = true;
  /** Noun used in the "Showing x to y of z …" line. Defaults to the generic
   *  wording the job/candidate listings already render. */
  @Input() itemLabel: string = 'results';
  /** Optional qualifier appended after itemLabel, e.g. `starting with "A"`,
   *  giving `Showing 1 to 16 of 45 companies starting with "A"`. Empty by
   *  default so existing callers are unaffected. */
  @Input() itemSuffix: string = '';
  /** 'default' is the job/candidate listing pager: narrow column, edge
   *  buttons hidden when they'd do nothing, ellipsis window.
   *  'directory' is the members/company wireframe pager: first/prev/next/last
   *  always present (greyed at the edges), chevron icons, and a plain window
   *  of consecutive page numbers with no ellipsis. It renders 8 buttons at
   *  all times, so it needs a full-width container. */
  @Input() variant: 'default' | 'directory' = 'default';
  @Output() pageChange = new EventEmitter<number>();

  get isDirectory(): boolean {
    return this.variant === 'directory';
  }

  // Configuration properties
  currentPage: number = 1;
  totalItems: number = 0;
  itemsPerPage: number = 10;
  maxVisiblePages: number = 5;
  showFirstLast: boolean = true;
  showPrevNext: boolean = true;
  baseRoute: string = './';
  queryParams: { [key: string]: any } = {};

  // Computed properties
  totalPages: number = 0;
  visiblePages: (number | string)[] = [];

  // Direction of the last navigation (set in updateConfiguration when
  // the incoming `currentPage` differs from the stored one). Drives the
  // bias in `generateVisiblePages` so the strip always reveals the page
  // the user is heading *toward*: after a forward click, the NEXT page
  // sits inside the window; after a backward click, the PREVIOUS page
  // sits inside the window. Initial state is 'forward' so the very first
  // render on page 1 shows [1, 2] rather than [0, 1] → [1, 2] via clamp.
  private lastDirection: 'forward' | 'backward' = 'forward';

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.updateConfiguration();
    this.calculatePagination();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config']) {
      this.updateConfiguration();
      this.calculatePagination();
    }
  }

  private updateConfiguration() {
    if (this.config) {
      const newPage = this.config.currentPage || 1;
      // Prefer the parent-supplied direction (survives recreation when
      // the *ngIf around this component flips during loadAPIcall).
      // Fall back to a same-instance currentPage diff only on actual
      // page changes — listings re-emit config on totalItems / other
      // field updates without the user navigating, and those shouldn't
      // flip the bias.
      if (this.config.direction) {
        this.lastDirection = this.config.direction;
      } else if (newPage !== this.currentPage) {
        this.lastDirection = newPage < this.currentPage ? 'backward' : 'forward';
      }
      this.currentPage = newPage;
      this.totalItems = this.config.totalItems || 0;
      this.itemsPerPage = this.config.itemsPerPage || 10;
      this.maxVisiblePages = this.config.maxVisiblePages || 5;
      this.showFirstLast = this.config.showFirstLast !== false;
      this.showPrevNext = this.config.showPrevNext !== false;
      this.baseRoute = this.config.baseRoute || './';
      this.queryParams = this.config.queryParams || {};
    }
  }

  private calculatePagination() {
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    this.visiblePages = this.generateVisiblePages();
  }

  private generateVisiblePages(): (number | string)[] {
    if (this.isDirectory) {
      return this.generateDirectoryPages();
    }

    const pages: (number | string)[] = [];

    // Few enough pages — show every number, no ellipses.
    // Includes total=3 because page 1's window is 3-wide ([1,2,3]) and
    // already covers the whole range without needing a "…".
    if (this.totalPages <= 3) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    // ===== Numeric window — user-specified shape =====
    //   page 1            → [1, 2, 3]                            trailing …
    //   page N forward    → leading … + [N, N+1]   + trailing …  (current LEFT)
    //   page N backward   → leading … + [N-1, N]   + trailing …  (current RIGHT)
    //   page near last    → forward window clamped so it doesn't spill past total
    //   page near 1 (bwd) → backward window clamped so it doesn't spill below 1
    // No anchor `1` after a leading "…" and no `totalPages` after a trailing
    // "…" — the «« / »» buttons own edge jumps, so the strip stays narrow
    // enough for 3-digit numbers to fit in the listing column.
    let startPage: number;
    let endPage: number;

    if (this.currentPage === 1) {
      // At the start: 3-wide window so the user sees what's next.
      startPage = 1;
      endPage = 3;
    } else if (this.lastDirection === 'backward') {
      // Backward: current sits on the RIGHT of the window.
      startPage = this.currentPage - 1;
      endPage = this.currentPage;
    } else {
      // Forward: current sits on the LEFT of the window.
      startPage = this.currentPage;
      endPage = this.currentPage + 1;
    }

    // Clamp end (forward bias overshooting past total pulls window back).
    if (endPage > this.totalPages) {
      endPage = this.totalPages;
      startPage = Math.max(1, endPage - 1);
    }
    // Clamp start (backward bias underflowing below 1 pulls window forward).
    if (startPage < 1) {
      startPage = 1;
      endPage = Math.min(this.totalPages, startPage + 1);
    }

    // Leading "…" — there's at least one page hidden before the window.
    if (startPage > 1) {
      pages.push('...');
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Trailing "…" — at least one page hidden after the window. Just a "…",
    // no `totalPages` anchor: keeps the strip short and consistent (»» jumps
    // to last).
    if (endPage < this.totalPages) {
      pages.push('...');
    }

    return pages;
  }

  // Directory window: a run of consecutive page numbers that slides with the
  // current page, clamped so it never spills past either end, with a "…"
  // marker on whichever side still has pages hidden behind it. No edge
  // anchors — the always-present « / » buttons own the jumps to first and
  // last, so the strip stays a predictable width.
  private generateDirectoryPages(): (number | string)[] {
    const span = Math.max(1, this.maxVisiblePages);
    const width = Math.min(span, this.totalPages);
    const start = Math.min(
      Math.max(1, this.currentPage - 1),
      Math.max(1, this.totalPages - width + 1)
    );
    const end = start + width - 1;

    const pages: (number | string)[] = [];
    if (start > 1) {
      pages.push('...');
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (end < this.totalPages) {
      pages.push('...');
    }
    return pages;
  }

  getQueryParams(page: number | string): { [key: string]: any } {
    return {
      ...this.queryParams,
      page: page
    };
  }

  onPageClick(page: number | string, event: Event) {
    if (typeof page === 'string' || page === this.currentPage) {
      event.preventDefault();
      return;
    }

    // Emit the page change event for parent component
    this.pageChange.emit(page);
    
    // The routing will be handled by routerLink, but we can add additional logic here if needed
  }

  getStartItem(): number {
    return Math.min((this.currentPage - 1) * this.itemsPerPage + 1, this.totalItems);
  }

  getEndItem(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
  }

  // Public methods for programmatic navigation
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.router.navigate([this.baseRoute], {
        queryParams: this.getQueryParams(page),
        relativeTo: this.activatedRoute
      });
    }
  }

  goToFirst() {
    this.goToPage(1);
  }

  goToLast() {
    this.goToPage(this.totalPages);
  }

  goToNext() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  goToPrevious() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }
}